import type { CurrentSessionUser } from '@entities/Authentication/types.ts';
import { getCurrentTimeWithReminder15 } from '@entities/schedules/schedule/helpers.tsx';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import axios, { AxiosError } from 'axios';
import { format, parse } from 'date-fns';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import addDays from 'date-fns/addDays';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import intervalToDuration from 'date-fns/intervalToDuration';
import isEqual from 'date-fns/isEqual';
import parseISO from 'date-fns/parseISO';
import startOfDay from 'date-fns/startOfDay';
import { DateTime } from 'luxon';
import type { Video } from '@shared/types/common';
import type { ExerciseVideo } from '@shared/types/exercises';
import type { Program, ProgramList } from '@shared/types/programs';
import type { PromoVideo } from '@shared/types/promo-videos';
import type { Client, IAm, User } from '@shared/types/users';

// TODO везде, где используется fieldToUppercase переписать на css (text-transform:capitalize) + capitalizeString перед отправкой на бекенд
export const fieldToUppercase = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  const value = e.target.value;
  const restOfString = value.slice(1);
  return value.length ? `${value[0].toUpperCase()}${restOfString}` : '';
};

export const capitalizeString = (inputStr: string | null) => {
  if (inputStr === null) return null;
  const value = inputStr;
  const restOfString = value.slice(1);
  return value.length ? `${value[0].toUpperCase()}${restOfString}` : '';
};

type FormatCountOptions = {
  decimals?: number; // сколько знаков после запятой (по умолчанию 0)
};

export function formatCount(count: number, opts: FormatCountOptions = {}): string {
  const { decimals = 0 } = opts;

  if (!Number.isFinite(count)) return String(count);

  const sign = count < 0 ? '-' : '';
  const n = Math.abs(count);

  if (n < 1000) return sign + String(n);

  if (n < 1_000_000) {
    const value = n / 1000;
    return sign + trimZeros(value.toFixed(decimals)) + 'K';
  }

  const value = n / 1_000_000;
  return sign + trimZeros(value.toFixed(decimals)) + 'M';
}

function trimZeros(s: string): string {
  return s.replace(/\.0+$|(\.\d*[1-9])0+$/, '$1');
}

export const getSessionBlock = (
  session: { start: Date; end: Date },
  tz: string,
  startTime?: string,
  endTime?: string,
  id?: number
) => {
  if (!startTime || !endTime) return 'single';
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const timeZoneChanged = browserTz !== tz;

  const start = timeZoneChanged ? utcToZonedTime(parseISO(startTime), tz) : parseISO(startTime);
  const end = timeZoneChanged ? utcToZonedTime(parseISO(endTime), tz) : parseISO(endTime);

  // const startReminder = doCalc15MinutesReminder(start);
  // const endReminder = doCalc15MinutesReminder(end);

  // const sessionStartClone = structuredClone(session.start);
  // const sessionEndClone = structuredClone(session.end);
  //
  // const sessionStartPlusReminderMinutes = sessionStartClone.setMinutes(
  //   sessionStartClone.getMinutes() + startReminder
  // );
  // const sessionStartPlusReminder = new Date(sessionStartPlusReminderMinutes);
  //
  // const sessionEndPlusReminderMinutes = sessionEndClone.setMinutes(
  //   sessionEndClone.getMinutes() + startReminder
  // );

  const sessionStartPlusReminder = getCurrentTimeWithReminder15(session.start, start);
  const sessionEndPlusReminder = getCurrentTimeWithReminder15(session.end, start);

  let type = 'start';
  // let type = 'single';

  // if (differenceInMinutes(end, start) <= 15) {
  //   type = 'single';
  // } else if (isEqual(sessionStartPlusReminder, start) && sessionEndPlusReminder < end) {
  //   type = 'start';
  // } else if (isEqual(sessionEndPlusReminder, end) && sessionStartPlusReminder > start) {
  //   type = 'end';
  // } else if (sessionStartPlusReminder > start && sessionEndPlusReminder < end) {
  //   type = 'middle';
  //   // } else if (isEqual(session.start, start) && session.end < end) {
  // }
  const lastSessionDiff = differenceInMinutes(end, session.start);

  const firstSessionDiff = differenceInMinutes(sessionStartPlusReminder, start);
  const firstMinusLast = firstSessionDiff - lastSessionDiff;
  const periodDiff = differenceInMinutes(end, start);

  if (periodDiff <= 15) {
    // const diff = periodDiff - 15;
    // if (diff === )
    type = 'single';
  } else if (lastSessionDiff >= 15 && firstSessionDiff >= 15) {
    type = 'middle';
  }
  // else if (lastSessionDiff <= 25 && firstSessionDiff > 15) {
  //   type = 'end';
  // }
  if (lastSessionDiff <= 15 && firstSessionDiff >= 15) {
    type = 'end';
  }

  const currentDayStart = startOfDay(session.start);
  const nextDayStart = addDays(currentDayStart, 1);

  if (currentDayStart > start && isEqual(currentDayStart, session.start)) {
    type = type + '_topOverlap';
  } else if (end > nextDayStart && isEqual(nextDayStart, session.end)) {
    type = type + '_bottomOverlap';
  }

  if (id && id === 1350) {
    // console.log('periodDiff:', periodDiff);
    // console.log('firstMinusLast:', firstMinusLast);
    console.log('type: ', type);
    console.log('LastDiff:', lastSessionDiff);
    console.log('Firstdiff:', firstSessionDiff);
    // console.log('availableStart', start)
    console.log('SessionStart', session.start);
    // console.log('SessionStartRem', sessionStartPlusReminder)
    // console.log('availableEnd', end)
    // console.log('SessionEnd', session.end)
    // console.log('SessionEndRem', sessionEndPlusReminder)
    // console.log('type: ', type);
  }

  return type;
};

export const isEmptySessionNow = (session: { start: Date; end: Date }) => {
  const { start, end } = session;
  const current = new Date();
  return start <= current && current <= end;
};

export const getInitial = (firstName: string, lastName: string, midleName?: string): string => {
  return `${lastName} ${firstName[0] + '.'}${midleName ? midleName[0] + '.' : ''}`;
};

export function numberWithSeparator(value: string | number | number[], char = ' ') {
  let val: string | number[] = '';
  if (typeof value === 'string') {
    val = value;
    return val.replace(/\B(?=(\d{3})+(?!\d))/g, char);
  }
  if (typeof value === 'number') {
    val = value.toString();
    return val.replace(/\B(?=(\d{3})+(?!\d))/g, char);
  }
  if (Array.isArray(value)) {
    val = value;
    return [
      val[0].toString().replace(/\B(?=(\d{3})+(?!\d))/g, char),
      val[1].toString().replace(/\B(?=(\d{3})+(?!\d))/g, char),
    ];
  }
}

export const getFullName = (user: IAm | User | Client) => {
  return `${[user.last_name, user.first_name, user.middle_name].join(' ').trim()}`;
};

export const getUserGender = (user?: CurrentSessionUser) => {
  if (!user) return '';
  return user.gender === 'm' ? 'Мужчина' : 'Женщина';
};

export const getUserBirthDay = (user?: CurrentSessionUser) => {
  if (!user) return '';
  if (!user.birth_date) return '';
  const date = parse(user.birth_date, 'yyyy-MM-dd', new Date());
  return format(date, 'dd.MM.yyyy');
};

export const getDaysLeft = (program: Program | ProgramList) => {
  let daysLeft = 0;
  const { individual } = program;

  const { duration_days } = individual || {};

  if (duration_days) daysLeft = duration_days;
  return daysLeft;
};

export const getVideoDuration = (videoDuration: string) => {
  const durationInseconds = parseInt(videoDuration);
  if (durationInseconds === 0 || isNaN(durationInseconds)) return '00:00';
  const duration = intervalToDuration({ start: 0, end: durationInseconds * 1000 });
  const { hours, minutes, seconds } = duration;
  let durationString = '';
  if (hours)
    durationString = durationString.concat(`${hours < 10 ? '0' + hours + ':' : hours + ':'}`);
  if (minutes) {
    durationString = durationString.concat(`${minutes < 10 ? '0' + minutes + ':' : minutes + ':'}`);
  } else {
    durationString = durationString.concat(`00:`);
  }

  if (seconds) {
    durationString = durationString.concat(`${seconds < 10 ? '0' + seconds : seconds}`);
  } else {
    durationString.concat(`00`);
  }

  return durationString;
};

export const getFullNameM = (first_name = '', middle_name = '', last_name = '') => {
  let fullName = '';

  last_name && (fullName += `${last_name} `);
  first_name && (fullName += `${first_name} `);
  middle_name && (fullName += middle_name);

  return fullName.trim();
};
export const getInitialM = (first_name = '', middle_name = '', last_name = '') => {
  let fullName = '';

  last_name && (fullName += `${last_name} `);
  first_name && (fullName += `${first_name[0]}. `);
  middle_name && (fullName += `${middle_name[0]}.`);

  return fullName.trim();
};

export const doFormatBigNumbers = (num: number | string) => {
  /// изменяет числа 1000 на 1 000
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};
/**
 * isIncludeInSearchParams - функция, которая проверяет, есть ли айди данной сущности в строке
 * @param id - айди сущности
 * @param searchParams - существующая строка search параметра
 */
export const isIncludeInSearchParams = (id: string, searchParams?: string): boolean => {
  return searchParams ? searchParams.split(',').some((idFromParams) => idFromParams === id) : false;
};

/**
 * deleteFromSearchParams - функция, которая удаляет айди из строки определенного search параметра
 * @param id - айди сущности
 * @param searchParams - существующая строка search параметра
 */
export const deleteFromSearchParams = (id: string, searchParams?: string): string => {
  return searchParams
    ? searchParams
        .split(',')
        .filter((idFromParams) => idFromParams !== id)
        .join(',')
    : '';
};

/**
 * addToSearchParams - функция, которая добавляет в search параметр айди сущности
 * @param id - айди сущности
 * @param searchParams - существующая строка search параметра
 */
export const addToSearchParams = (id: string, searchParams: string): string => {
  return searchParams === '' ? String(id) : `${searchParams},${id}`;
};

/**
 * doDeleteOrAddToSearchParams - функция, которая определяет, есть ли айди сущности в строке,
 * если нет, добавляет - в обратном случае удаляет этот айди из строки
 * Возвращает search параметр.
 * @param id - айди сущности
 * @param searchParams - существующая строка search параметра
 */
export const doDeleteOrAddToSearchParams = (id: string, searchParams: string): string => {
  return isIncludeInSearchParams(id, searchParams)
    ? deleteFromSearchParams(id, searchParams)
    : addToSearchParams(id, searchParams);
};

// /**
//  * Прости меня мама, прости отец. Я не хотел, меня заставили...
//  * doParamsGetReadyForSendToBack - форматирует параметры в нужный для бека вид.
//  * Это необходимо бизнесу, у него там крайне корявая обработка search параметров :(
//  * @param params - все uri параметры
//  */
// export const doParamsGetReadyForSendToBack = (params?: { [key: string]: string }): string => {
//   let output = '';
//
//   for (const param in params) {
//     params[param] && params[param].split(',').forEach((el) => (output += `${param}=${el}&`));
//   }
//
//   return output === '' ? output : '?' + output;
// };

export function formatPhoneNumber(phoneNumber: string) {
  const clearPhoneNumber = phoneNumber.replace(/^\+/g, '');
  const countryCode = clearPhoneNumber.slice(0, 1);
  const areaCode = clearPhoneNumber.slice(1, 4);
  const firstPart = clearPhoneNumber.slice(4, 7);
  const secondPart = clearPhoneNumber.slice(7, 9);
  const thirdPart = clearPhoneNumber.slice(9, 11);

  return `${countryCode} ${areaCode} ${firstPart}-${secondPart}-${thirdPart}`;
}

export const isPhoneNumber = (value: string) => {
  const cleanNumber = value.replace(/[' ', -]/g, '');
  return !isNaN(Number(cleanNumber)) && cleanNumber.length === 11;
};
/**
 * convert ur object to FormData instance and return that instance
 * @param data - object - data that u want
 */
export const doConvertDataToFormData = (data: Record<string, unknown>) => {
  const formData = new FormData();

  for (const field in data) {
    typeof data[field] !== 'undefined' &&
      (typeof data[field] === 'object'
        ? formData.append(field, JSON.stringify(data[field]))
        : formData.append(field, data[field] as string));
  }

  return formData;
};
/**
 * concatenate to difference form data objects
 * @param firstFormDataObject {FormData}
 * @param secondFormDataObject {FormData}
 */
export const doAddFormDataToFormData = (
  firstFormDataObject: FormData,
  secondFormDataObject?: FormData
): FormData => {
  const dataToReturn = firstFormDataObject;
  secondFormDataObject &&
    secondFormDataObject.forEach((value, key) => firstFormDataObject.append(key, value));
  return dataToReturn;
};
/**
 * Данная функция нужна для проверки есть ли массив и есть ли в нем хоть один элемент
 * @param array {Array<unknown>} - массив чего угодно
 */
export const doCheckArray = (array: Array<unknown> | undefined) =>
  array && (array.length === 0 ? undefined : array);

export const enableScroll = () => document.body.classList.remove('ReactModal__Body--open');

export const getCosts = (baseCost: number, costs: any[]) => {
  const sortGroupCosts = [...costs].sort((a, b) => a.cost - b.cost);
  const maxCost = baseCost > sortGroupCosts.at(-1)?.cost ? baseCost : sortGroupCosts.at(-1)?.cost;

  const costsValues: Record<string, number> = {
    min: sortGroupCosts[0].cost,
    max: maxCost,
    base: baseCost,
  };
  return costsValues;
};

export const scrollFast = (valueY: number) => {
  document.body.style.scrollBehavior = 'none';
  window.scrollTo(0, valueY);
  document.body.style.scrollBehavior = '';
};
/**
 * areArraysWithNumbersEqual - функция, которая сравнивает два массива чисел.
 * Числа могут быть в разном порядке.
 * Если все числа одного массива содержаться в втором - она вернёт true. Иначе false.
 * @param secondArray {number[]}
 * @param firstArray {number[]}
 */
export const areArraysWithNumbersEqual = ({
  secondArray,
  firstArray,
}: {
  firstArray?: number[];
  secondArray?: number[];
}): boolean =>
  (firstArray?.length === secondArray?.length &&
    firstArray?.every((el) => secondArray?.includes(el))) ||
  false;

/**
 * areArraysWithNumbersEqualStrict - функция, которая сравнивает два массива чисел.
 * Числа должны идти в строгом порядке.
 * Если все числа одного массива содержаться во втором - она вернёт true. Иначе false.
 * @param secondArray {number[]}
 * @param firstArray {number[]}
 */
export const areArraysWithNumbersEqualStrict = ({
  secondArray,
  firstArray,
}: {
  firstArray?: number[];
  secondArray?: number[];
}): boolean => {
  let output = true;

  if (!firstArray && secondArray) {
    output = false;
  }

  if (!secondArray && firstArray) {
    output = false;
  }

  if (firstArray?.length !== secondArray?.length) {
    output = false;
  }

  firstArray?.forEach((e, i) => secondArray?.at(i) !== e && (output = false));

  return output;
};
/**
 * realize the Adapter pattern with setTimeout function
 * @param {function} cb
 */
export const withTimeout = (cb: () => void) => setTimeout(cb, 0);

export const getCommonTabs = ({ can_sell, isAuth }: { isAuth: boolean; can_sell: boolean }) => {
  const output: Record<string, string> = {};

  if (isAuth) {
    output.all = 'Все';
    output.favorite = 'Избранные';
  }

  if (can_sell) {
    output.my = 'Мои';
  }

  return output;
};

export const doTransformRTKErrorResponse = (error: FetchBaseQueryError | AxiosError) => {
  const output: {
    firstError?: string;
    firstErrorFiledName?: string;
    status?: number | 'FETCH_ERROR' | 'PARSING_ERROR' | 'TIMEOUT_ERROR' | 'CUSTOM_ERROR';
    data?: Record<string, Array<string> | string>;
  } = {};
  if (error?.status) {
    output.status = error.status;
  }
  let allErrorRecords: Record<string, Array<string> | string> = {};

  if (axios.isAxiosError(error)) {
    if (error?.response?.data) {
      allErrorRecords = error?.response?.data as Record<string, Array<string> | string>;
    }
  } else {
    if (error?.data) {
      allErrorRecords = error.data as Record<string, Array<string> | string>;
    }
  }

  if (allErrorRecords.hasOwnProperty('root') && allErrorRecords['root'] === 'ValidationError') {
    const newObj: Record<string, Array<string> | string> = {};
    for (const prop in allErrorRecords) {
      if (prop !== 'root') {
        newObj[prop] = allErrorRecords[prop];
      }
    }

    allErrorRecords = newObj;
  }

  if (Object.keys(allErrorRecords).length) {
    output.data = allErrorRecords;
    const allErrorRecordsKeys = Object.keys(allErrorRecords);
    output.firstErrorFiledName = allErrorRecordsKeys[0];
    const firstErrorField = allErrorRecords[allErrorRecordsKeys[0]];
    if (Array.isArray(firstErrorField)) {
      output.firstError = firstErrorField[0];
    } else {
      output.firstError = firstErrorField;
    }
  }
  return output;
};

export const doFormatEmail = (email?: string) => {
  if (email && email.length > 70) {
    return `${email.substring(0, 3)}*****${email.substring(email.length - 3, email.length)}`;
  } else {
    return email;
  }
};

export const base64ToFile = (base64: string, filename: string): File => {
  const arr = base64.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
};

export const base64ToBlob = (base64: string): Blob => {
  const arr = base64.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
};

export const getIsVideoAvailableBySchedule = ({
  video,
  program,
}: {
  program?: Program;
  video?: ExerciseVideo | PromoVideo | Video;
}) => {
  let output = true;
  if (program?.schedule) {
    const currentVideoScheduleObj = program.schedules.find((el) => el.videoId === video?.id);
    if (currentVideoScheduleObj && !isScheduledVideoAvailable(currentVideoScheduleObj.date)) {
      output = false;
    }
  }
  return output;
};

export const isScheduledVideoAvailable = (date: string): boolean => {
  // Преобразуем строку в DateTime
  const scheduled = DateTime.fromFormat(date, "yyyy-MM-dd'T'HH:mm:ssZ");
  // Сравниваем с текущей датой
  return scheduled <= DateTime.now();
};
